import {
  applicable,
  checklistTemplates,
  isClosed,
  type Check,
  type Document,
  type Dossier,
} from './dueDiligence';
import {
  newPlanning,
  type Planning,
  type ProjectTask,
} from './purchasePlanning';
import { maintenanceTasks, type MaintenanceState } from './maintenance';
import { costEvidence } from './costEvidence';

export type GuidedTask = ProjectTask & {
  source: 'manual' | 'check' | 'cost' | 'finance' | 'maintenance';
  sourceId: string;
};
const reserved = (id: string) => /^(check|cost|finance|maintenance):/.test(id);
export function isUnresolvedIssue(dossier: Dossier, task: GuidedTask): boolean {
  return (
    task.source === 'check' &&
    task.status !== 'done' &&
    dossier.checks[task.sourceId]?.status === 'issue'
  );
}
// Linked tasks are views of their source. Completing a task must never close an
// expert check, invent a quote, or mark a bank response received.
export function guidedTasks(
  dossier: Dossier,
  documents: Document[],
  maintenance?: MaintenanceState,
): GuidedTask[] {
  const planning = dossier.planning ?? newPlanning();
  const items: GuidedTask[] = planning.tasks
    .filter((task) => !reserved(task.id))
    .map((task) => ({ ...task, source: 'manual', sourceId: task.id }));
  const metadata = (id: string) =>
    planning.tasks.find((task) => task.id === id);
  for (const template of checklistTemplates.filter((template) =>
    applicable(template, dossier),
  )) {
    const check = dossier.checks[template.id];
    if (!check) continue;
    const phase =
      template.phase === 'purchase'
        ? 'assessment'
        : (template.phase as 'handover' | 'ownership');
    const current =
      dossier.stage === 'ownership'
        ? template.phase === 'ownership'
        : dossier.stage === 'handover'
          ? template.phase !== 'ownership'
          : template.phase === 'purchase';
    const activeQuestion =
      ['issue', 'waiting'].includes(check.status) ||
      Boolean(
        check.nextAction ||
          check.dueDate ||
          check.assignee ||
          check.evidence.length,
      );
    const closed = isClosed(template, check, documents);
    if (!current && !(activeQuestion && !closed)) continue;
    const id = `check:${template.id}`;
    const settings = metadata(id);
    items.push({
      id,
      source: 'check',
      sourceId: template.id,
      title: check.nextAction.trim() || template.title,
      owner: check.assignee,
      dueDate: check.dueDate,
      status: closed ? 'done' : check.status === 'waiting' ? 'waiting' : 'todo',
      phase,
      repeatMonths: 0,
      reminderDays: settings?.reminderDays ?? 1,
      documentId:
        check.evidence.find((evidence) =>
          documents.some((document) => document.id === evidence.documentId),
        )?.documentId ?? '',
      note: check.notes || template.question,
    });
  }
  for (const cost of planning.costs.filter((cost) =>
    ['inspection', 'repair', 'fees'].includes(cost.category),
  )) {
    const id = `cost:${cost.id}`;
    const settings = metadata(id);
    const latest = cost.revisions.at(-1);
    const documented = costEvidence(cost, documents) === 'linked';
    items.push({
      id,
      source: 'cost',
      sourceId: cost.id,
      title: `${latest ? 'Kostenstand belegen' : 'Kostenposition bewerten'}: ${cost.title}`,
      owner: settings?.owner ?? '',
      dueDate: settings?.dueDate ?? '',
      status: documented ? 'done' : 'todo',
      phase: cost.dueMonth >= 12 ? 'ownership' : 'assessment',
      repeatMonths: 0,
      reminderDays: settings?.reminderDays ?? 1,
      documentId: latest?.documentId ?? '',
      note:
        latest?.note ||
        'Umfang, Ausschlüsse und Bruttoangebot einholen. Unbekannte Kosten sind kein Null-Euro-Risiko.',
    });
  }
  for (const request of planning.requests) {
    const id = `finance:${request.id}`;
    const settings = metadata(id);
    const done =
      request.status === 'declined' ||
      (request.status === 'offered' &&
        documents.some((document) => document.id === request.documentId));
    const action =
      request.status === 'draft'
        ? 'Anfrage vorbereiten'
        : request.status === 'documents_needed'
          ? 'Unterlagen ergänzen'
          : request.status === 'offered'
            ? 'Angebot belegen'
            : 'Antwort nachfassen';
    items.push({
      id,
      source: 'finance',
      sourceId: request.id,
      title: `${request.lender}: ${done ? 'Anfrage abgeschlossen' : action}`,
      owner: settings?.owner ?? '',
      dueDate: request.dueDate,
      status: done
        ? 'done'
        : request.status === 'requested'
          ? 'waiting'
          : 'todo',
      phase: 'negotiation',
      repeatMonths: 0,
      reminderDays: settings?.reminderDays ?? 1,
      documentId: request.documentId,
      note: request.note,
    });
  }
  items.push(...maintenanceTasks(maintenance, documents));
  return items.sort(
    (a, b) =>
      (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0) ||
      (a.dueDate || '9999').localeCompare(b.dueDate || '9999') ||
      Number(isUnresolvedIssue(dossier, b)) -
        Number(isUnresolvedIssue(dossier, a)) ||
      a.title.localeCompare(b.title, 'de'),
  );
}
export function updateGuidedTask(dossier: Dossier, task: GuidedTask): Dossier {
  if (task.source === 'maintenance') return dossier;
  const planning = dossier.planning ?? newPlanning();
  const entry: ProjectTask = {
    id: task.id,
    title: task.title,
    owner: task.owner,
    dueDate: task.dueDate,
    status: task.status,
    phase: task.phase,
    repeatMonths: task.source === 'manual' ? task.repeatMonths : 0,
    reminderDays: task.reminderDays,
    documentId: task.documentId,
    note: task.note,
  };
  const next: Planning = {
    ...planning,
    tasks: planning.tasks.some((item) => item.id === task.id)
      ? planning.tasks.map((item) => (item.id === task.id ? entry : item))
      : [...planning.tasks, entry],
  };
  if (task.source === 'check') {
    const old = dossier.checks[task.sourceId];
    if (!old) return dossier;
    const template = checklistTemplates.find(
      (template) => template.id === task.sourceId,
    );
    const nextAction =
      task.title === (old.nextAction.trim() || template?.title)
        ? old.nextAction
        : task.title;
    const notes =
      task.note === (old.notes || template?.question) ? old.notes : task.note;
    const changed =
      old.assignee !== task.owner ||
      old.dueDate !== task.dueDate ||
      old.nextAction !== nextAction ||
      old.notes !== notes;
    const check: Check = {
      ...old,
      assignee: task.owner,
      dueDate: task.dueDate,
      nextAction,
      notes,
      ...(changed && ['resolved', 'not_applicable'].includes(old.status)
        ? { status: 'open' as const, reviewedAt: '' }
        : {}),
    };
    return {
      ...dossier,
      planning: next,
      checks: { ...dossier.checks, [task.sourceId]: check },
    };
  }
  if (task.source === 'finance')
    next.requests = planning.requests.map((request) =>
      request.id === task.sourceId
        ? { ...request, dueDate: task.dueDate }
        : request,
    );
  return { ...dossier, planning: next };
}
